import { Component, signal, resource, inject, ViewChild, linkedSignal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { CompanyProfileService } from '../services/company-profile.service';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { TextboxComponent } from 'src/app/controls/components/basic/textbox/textbox.component';
import { ImageUploaderComponent } from 'src/app/controls/components/basic/image-uploader/image-uploader.component';
import { PageCommandComponent } from 'src/app/controls/components/misc/page-command/page-command.component';
import { PageCommandGroupComponent } from 'src/app/controls/components/misc/page-command-group/page-command-group.component';
import { FormMode, FormPageComponent } from 'src/app/controls/components/template/form-page/form-page.component';
import { LayoutRowComponent } from 'src/app/controls/components/basic/layout-row/layout-row.component';
import { LayoutColumnComponent } from 'src/app/controls/components/basic/layout-column/layout-column.component';
import { ConfigurationService } from '../services/configuration.service';
import { CheckboxComponent } from 'src/app/controls/components/basic/checkbox/checkbox.component';

@Component({
    selector: 'app-company-profile',
    standalone: true,
    templateUrl: './company-profile.component.html',
    styleUrl: './company-profile.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        TextboxComponent,
        ImageUploaderComponent,
        PageCommandComponent,
        PageCommandGroupComponent,
        FormPageComponent,
        LayoutRowComponent,
        LayoutColumnComponent,
        CheckboxComponent
    ],
})
export class CompanyProfileComponent {
    @ViewChild('tForm')
    private form: NgForm;
    private profileService = inject(CompanyProfileService);
    private toastService = inject(ToastService);
    private configurationService = inject(ConfigurationService);

    FormMode = FormMode; // for the HTML to use formmode.
    mode = signal<FormMode>(FormMode.View);    

    // Resources and linked signal for company profile
    companyProfileResource = resource({
        loader: () => this.profileService.getGetCompanyProfile(),
    });
    companyProfile = linkedSignal(() => this.companyProfileResource.value());

    // Resource and linked signal for the "Show Logo on Partner Portal" setting
    showLogoOnPartnerPortalResource = resource({
        loader: () => this.configurationService.getEffectiveSettingBoolean('ShowCompanyLogoOnPartnerPortal'),
    });
    showLogoOnPartnerPortal = linkedSignal(() => this.showLogoOnPartnerPortalResource.value());    


    updateLogo(value: string | null): void {
        this.companyProfile.update((p) => {
            if (!p) return p;
            return { ...p, Logo: value };
        });
    }

    updateName(value: string): void {
        this.companyProfile.update((p) => {
            if (!p) return p;
            return { ...p, Name: value };
        });
    }

    // Method to handle the change
    onShowLogoOnPartnerPortalChange = (value: boolean): void => {
        this.showLogoOnPartnerPortalResource.set(value);
    };

    onSaveClick = async (): Promise<void> => {
        const profile = this.companyProfile();
        let logoSetting = this.showLogoOnPartnerPortal();
        
        if (!profile) return;

        // If the logo is null/empty, force showLogoOnPartnerPortal to false
        if (!profile.Logo) {
            logoSetting = false;
        }

        try {
            // Save the profile
            await this.profileService.saveCompanyProfile(profile);
            await this.configurationService.saveEffectiveSettingBoolean('ShowCompanyLogoOnPartnerPortal', logoSetting);
            
            this.toastService.showToast(
                'Company profile saved',
                ToastType.success
            );
            
            // Reload resources
            this.companyProfileResource.reload();
            this.showLogoOnPartnerPortalResource.reload();
        } catch (err) {
            this.toastService.showToast(
                'Failed to save company profile',
                ToastType.error
            );
            console.error(err);
        }
    };

    onCancelClick = (): void => {
        this.companyProfileResource.reload();
        this.showLogoOnPartnerPortalResource.reload();
    };

    onModeChanged = (e: FormMode): void => {
        if (this.form) {
            if (e === FormMode.Edit) {
                this.form.form.enable();
            } else {
                this.form.form.disable();
            }
        }
    };
}
